import React from 'react'
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  DownloadIcon,
  EditIcon,
  ExternalLinkIcon,
  EyeIcon,
  MoreHorizontalIcon,
  Trash2Icon,
} from 'lucide-react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkBreaks from 'remark-breaks'
import remarkFrontmatter from 'remark-frontmatter'
import remarkGfm from 'remark-gfm'

import { type JobApplication } from '@/components/hooks/use-job-log'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { cn, downloadMarkdownFile } from '@/lib/utils'

import { MarkdownEditor } from '../resume-tailor/markdown-editor'
import { ResumeDownloader } from '../resume-tailor/resume-downloader'
import { JobEntryDialog, type JobEntryFormData } from './job-entry-dialog'

const statusColors: Record<string, string> = {
  applied: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  interviewing:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  offered: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  withdrawn: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
}

const workModeLabels: Record<string, string> = {
  remote: 'Remote',
  hybrid: 'Hybrid',
  onsite: 'On-site',
}

export type SortKey =
  | 'date'
  | 'position'
  | 'company'
  | 'workMode'
  | 'location'
  | 'salary'
  | 'status'

export type SortDirection = 'asc' | 'desc'

export interface SortConfig {
  key: SortKey
  direction: SortDirection
}

interface JobLogTableProps {
  applications: JobApplication[]
  onUpdate: (id: string, data: Partial<Omit<JobApplication, 'id'>>) => void
  onDelete: (id: string) => void
  sortConfig?: SortConfig
  onSortChange?: (config: SortConfig) => void
}

interface SortableHeaderProps {
  label: string
  sortKey: SortKey
  currentSort?: SortConfig
  onSort?: (config: SortConfig) => void
  className?: string
}

const SortableHeader = ({
  label,
  sortKey,
  currentSort,
  onSort,
  className,
}: SortableHeaderProps) => {
  const isActive = currentSort?.key === sortKey
  const direction = isActive ? currentSort.direction : null

  const handleClick = () => {
    if (!onSort) return

    let newDirection: SortDirection = 'asc'
    if (isActive) {
      newDirection = direction === 'asc' ? 'desc' : 'asc'
    }
    onSort({ key: sortKey, direction: newDirection })
  }

  return (
    <TableHead className={className}>
      <button
        onClick={handleClick}
        className={cn(
          'flex items-center gap-1 transition-colors hover:text-foreground',
          isActive ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
        {isActive ? (
          direction === 'asc' ? (
            <ArrowUpIcon className="h-3 w-3" />
          ) : (
            <ArrowDownIcon className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDownIcon className="h-3 w-3 opacity-50" />
        )}
      </button>
    </TableHead>
  )
}

export const JobLogTable = (props: JobLogTableProps) => {
  const { applications, onUpdate, onDelete, sortConfig, onSortChange } = props

  const [editingApplication, setEditingApplication] =
    React.useState<JobApplication | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<string | null>(
    null,
  )
  const [viewResumeApplication, setViewResumeApplication] =
    React.useState<JobApplication | null>(null)

  const handleEdit = (application: JobApplication) => {
    setEditingApplication(application)
  }

  const handleSaveEdit = (data: JobEntryFormData) => {
    if (editingApplication) {
      onUpdate(editingApplication.id, data)
      setEditingApplication(null)
    }
  }

  const handleConfirmDelete = () => {
    if (deleteConfirmId) {
      onDelete(deleteConfirmId)
      setDeleteConfirmId(null)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  if (applications.length === 0) {
    return null
  }

  return (
    <React.Fragment>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader
                label="Date"
                sortKey="date"
                currentSort={sortConfig}
                onSort={onSortChange}
                className="w-28"
              />
              <SortableHeader
                label="Position"
                sortKey="position"
                currentSort={sortConfig}
                onSort={onSortChange}
              />
              <SortableHeader
                label="Company"
                sortKey="company"
                currentSort={sortConfig}
                onSort={onSortChange}
              />
              <SortableHeader
                label="Mode"
                sortKey="workMode"
                currentSort={sortConfig}
                onSort={onSortChange}
                className="w-24"
              />
              <SortableHeader
                label="Location"
                sortKey="location"
                currentSort={sortConfig}
                onSort={onSortChange}
              />
              <SortableHeader
                label="Salary"
                sortKey="salary"
                currentSort={sortConfig}
                onSort={onSortChange}
              />
              <SortableHeader
                label="Status"
                sortKey="status"
                currentSort={sortConfig}
                onSort={onSortChange}
                className="w-28"
              />
              <TableHead className="w-12">Resume</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {applications.map((application) => (
              <TableRow key={application.id}>
                <TableCell className="font-medium text-nowrap">
                  {formatDate(application.date)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="max-w-48 truncate">
                      {application.position || '-'}
                    </span>
                    {application.url && (
                      <a
                        href={application.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLinkIcon className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <span className="max-w-32 truncate">
                    {application.company || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {workModeLabels[application.workMode] || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="max-w-32 truncate text-sm">
                    {application.location || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{application.salary || '-'}</span>
                </TableCell>
                <TableCell>
                  {application.status ? (
                    <Badge
                      variant="secondary"
                      className={statusColors[application.status] || ''}
                    >
                      {application.status.charAt(0).toUpperCase() +
                        application.status.slice(1)}
                    </Badge>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {application.resume ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setViewResumeApplication(application)}
                    >
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={(triggerProps) => (
                        <Button
                          {...triggerProps}
                          variant="ghost"
                          size="icon-sm"
                        >
                          <MoreHorizontalIcon className="h-4 w-4" />
                        </Button>
                      )}
                    />
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(application)}>
                        <EditIcon className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDeleteConfirmId(application.id)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2Icon className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <JobEntryDialog
        title="Edit Application"
        description="Update the details of your job application"
        open={!!editingApplication}
        onOpenChange={(open) => !open && setEditingApplication(null)}
        initialData={editingApplication || undefined}
        onSave={handleSaveEdit}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteConfirmId}
        onOpenChange={(open) => !open && setDeleteConfirmId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this job application? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resume Preview Dialog */}
      <Dialog
        open={!!viewResumeApplication}
        onOpenChange={(open) => !open && setViewResumeApplication(null)}
      >
        <DialogContent className="flex h-[80vh] max-h-[80vh] min-w-[80vw] flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Resume - {viewResumeApplication?.position} at{' '}
              {viewResumeApplication?.company}
            </DialogTitle>
            <DialogDescription>
              The tailored resume used for this application
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1">
            <div className="overflow-hidden bg-muted-foreground p-12">
              <div className="prose w-full max-w-full bg-white p-12">
                <Markdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm, remarkFrontmatter, remarkBreaks]}
                >
                  {viewResumeApplication?.resume}
                </Markdown>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!viewResumeApplication}
        onOpenChange={(open) => !open && setViewResumeApplication(null)}
      >
        <DialogContent className="flex h-[90vh] max-h-[90vh] min-w-[90vw] flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Resume - {viewResumeApplication?.position} at{' '}
              {viewResumeApplication?.company}
            </DialogTitle>

            <div className="flex items-center justify-between">
              <DialogDescription>
                The tailored resume used for this application
              </DialogDescription>
              <div className="mr-4 flex gap-2">
                <ResumeDownloader
                  filename={`${viewResumeApplication?.company}_resume`}
                  isStreaming={false}
                  resumeText={viewResumeApplication?.resume || ''}
                />
              </div>
            </div>
          </DialogHeader>
          <div className="flex h-full min-h-0 w-full min-w-0">
            <ScrollArea className="h-full max-h-full min-h-0 w-full max-w-full min-w-0">
              <div className="overflow-hidden bg-muted-foreground p-24">
                <div className="prose w-full max-w-full bg-white p-24">
                  <Markdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm, remarkFrontmatter, remarkBreaks]}
                  >
                    {viewResumeApplication?.resume}
                  </Markdown>
                </div>
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  )
}
